import { useSelector } from 'react-redux';
import { PiggyBank } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, Ref, useRef } from 'react';

import './index.css';
import {
  PlanCardHeaderSyled,
  CarrierLogoBoxStyled,
} from 'enrollment/features/Plans/PlanCard/PlanCardStyled';
import { selectEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import {
  BenefitDto,
  BenefitPlanDto,
  CoverageDto,
  PlanRiderDto,
} from 'enrollment/queries/useBenefitPlansQuery/types';
import {
  selectLifeEventCompleted,
  selectPerPayPeriodText,
} from 'enrollment/services';
import {
  AdditionalPlanElectionDataDto,
  ElectRequestDto,
} from 'enrollment/features/Plans/PlanCard/types';
import { useRetirementContributionsQuery } from 'enrollment/queries/useRetirementContributionsQuery';
import { PSBannerWrapper } from '../Shared';
import { PsButtonTuner, PsSelectTuner } from 'stencilSetup';
import { Loader } from 'theSource/components';
import { BeneficiaryDataCardAllocationErrorStyled } from 'enrollment/features/AddYourBeneficiaries/components/BeneficiaryDataCard/BeneficiariesDataCardStyled';
import { EsignatureModal } from 'enrollment/features/Plans/EsignatureModal';
import { RetirementContributionsResponseDto } from 'enrollment/queries/useRetirementContributionsQuery/utils';
import {
  Contribution401kContext,
  useContribution401kContext,
} from 'context/contribution401kContext';
import { RiderBenefits } from '../RiderBenefits';
import { mapElectedRiders } from 'enrollment/features/Plans/PlanCard/utils';
import {
  convertCurrency,
  getPremiumStyled,
  RETURN_DISPLAY,
} from 'enrollment/utils';
import { CategoryCoverageDto } from 'enrollment/queries/useBenefitCategoryQuery/types';

type RadioOption = 'percentage' | 'perPayPeriod';

interface ContributionProps {
  benefit: BenefitDto;
  coverage: CategoryCoverageDto | CoverageDto | undefined;
  plan: BenefitPlanDto;
  updateElection: (params: ElectRequestDto) => void;
  orgPlanYearId: number;
  showEsignatureModal?: boolean;
  benefitPlans?: BenefitPlanDto[];
}

type Contribution401kRadioProps = {
  orgEnrollmentMethod?: RetirementContributionsResponseDto['data']['org_enrollment_method'];
  setErrorMessage: (value: string | null) => void;
};

type Contribution401kInputsProps = {
  numberInputRef: Ref<HTMLInputElement>;
  increments: RetirementContributionsResponseDto['data']['increments'];
  maxContribution: number;
  setErrorMessage: (value: string | null) => void;
};

type Contribution401kFooterProps = {
  plan: BenefitPlanDto;
  buttonDisabled?: boolean;
  showEsignatureModal?: boolean;
  benefitPlans?: BenefitPlanDto[];
};

type DecideSelectedRadioParams = {
  orgEnrollmentMethod: RetirementContributionsResponseDto['data']['org_enrollment_method'];
  percentage?: CategoryCoverageDto | CoverageDto['req_increments'];
  dollarAmount?: CategoryCoverageDto | CoverageDto['req_subscriber_premium'];
};

export const Contribution401k = ({
  benefit,
  coverage,
  plan,
  orgPlanYearId,
  updateElection,
  showEsignatureModal,
  benefitPlans,
}: ContributionProps) => {
  const [selectedRadio, setSelectedRadio] = useState<RadioOption>();
  const [amountPercentage, setAmountPercentage] = useState<number | null>(null);
  const [eeContribution, setEeContribution] = useState<number | null>(null);
  const [electedRiders, setElectedRiders] = useState<number[]>([]);

  const enrollmentContextType = useSelector(selectEnrollmentContext);
  const lifeEventCompleted = useSelector(selectLifeEventCompleted);
  const { retirementContributionsData, isLoading, isFetching } =
    useRetirementContributionsQuery({
      orgPlanYearId: benefit.org_plan_year_id,
      enrollmentContext: enrollmentContextType,
      orgPlanId: plan.plan_id,
      lifeEventCompleted,
    });
  const loading = isFetching || isLoading;

  return (
    <div className="card p-0">
      <Contribution401kContext.Provider
        value={{
          benefit,
          enrollmentContextType,
          selectedRadio,
          amountPercentage,
          eeContribution,
          retirementContributionsData,
          loading,
          setAmountPercentage,
          setEeContribution,
          setSelectedRadio,
          updateElection,
          electedRiders,
        }}
      >
        <Contribution401k.Header plan={plan} />

        {loading ? <Loader /> : <Contribution401k.Body coverage={coverage} />}
        {!loading && (
          <RiderBenefits
            riderInfo={plan.org_plan_rider_benefits as PlanRiderDto[]}
            riderCoverages={coverage?.rider_coverages || []}
            planPremium={Number(eeContribution)}
            electedRiders={electedRiders}
            setElectedRiders={setElectedRiders}
            orgPlanYearId={orgPlanYearId}
            displayCheckbox
            key={benefit.org_benefit_name}
            smallMobileMargins={true}
          />
        )}
        {!loading && (
          <Contribution401k.Footer
            plan={plan}
            buttonDisabled={
              !eeContribution && !amountPercentage ? true : undefined
            }
            showEsignatureModal={showEsignatureModal}
            benefitPlans={benefitPlans}
          />
        )}
      </Contribution401kContext.Provider>
    </div>
  );
};

Contribution401k.Header = function Header({ plan }: { plan: BenefitPlanDto }) {
  const { t } = useTranslation('pages.financial');

  return (
    <PlanCardHeaderSyled>
      <div className="icon-container annotation2">
        <PiggyBank size={20} color="white" />
      </div>

      <h3 className="pl-3_5 m-0">{plan.plan_name}</h3>

      <CarrierLogoBoxStyled>
        {plan.carrier_logo_url ? (
          <img
            src={plan.carrier_logo_url}
            alt={plan.carrier || t('defaultLogoAltText')}
            data-testid="carrier-logo"
            style={{ maxHeight: '100%' }}
          />
        ) : (
          <h3 className="m-0">{plan.carrier}</h3>
        )}
      </CarrierLogoBoxStyled>
    </PlanCardHeaderSyled>
  );
};

Contribution401k.Body = function Body({
  coverage,
}: {
  coverage: CategoryCoverageDto | CoverageDto | undefined;
}) {
  const {
    selectedRadio,
    retirementContributionsData,
    setEeContribution,
    setAmountPercentage,
    setSelectedRadio,
  } = useContribution401kContext();
  const { t } = useTranslation('pages.financial');
  const numberInputRef = useRef<HTMLInputElement>(null);
  const [maxContribution, setMaxContribution] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const increments = retirementContributionsData?.data.increments || [];

  function decideSelectedRadio({
    orgEnrollmentMethod,
    percentage,
    dollarAmount,
  }: DecideSelectedRadioParams) {
    if (orgEnrollmentMethod !== 'annual_and_percent') {
      setSelectedRadio(
        orgEnrollmentMethod === 'enter_annual' ? 'perPayPeriod' : 'percentage'
      );
      return;
    }

    const formattedPercentage = Number(percentage);
    const formattedDollarAmount = Number(dollarAmount);
    if (
      formattedDollarAmount &&
      !Number.isNaN(formattedDollarAmount) &&
      (formattedPercentage === 0 || Number.isNaN(formattedPercentage))
    ) {
      setSelectedRadio('perPayPeriod');
    } else {
      setSelectedRadio('percentage');
    }
  }

  function setSelectedInputView({
    percentage,
    dollarAmount,
  }: {
    percentage?: CoverageDto['req_increments'];
    dollarAmount?: CoverageDto['req_subscriber_premium'];
  }) {
    if (percentage) {
      setEeContribution(Number(dollarAmount));
      setAmountPercentage(Number(percentage));
    } else if (dollarAmount) {
      const dollarValue = Number(dollarAmount);
      numberInputRef.current?.setAttribute('value', dollarValue.toString());
      setEeContribution(dollarValue);
    }
  }

  useEffect(() => {
    if (retirementContributionsData && increments.length > 0) {
      const orgEnrollmentMethod =
        retirementContributionsData.data.org_enrollment_method;
      const percentage = coverage?.req_increments;
      const dollarAmount = coverage?.req_subscriber_premium;

      decideSelectedRadio({ orgEnrollmentMethod, percentage, dollarAmount: dollarAmount !== undefined ? Number(dollarAmount) : undefined });
      setSelectedInputView({ percentage, dollarAmount: dollarAmount !== undefined ? Number(dollarAmount) : undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retirementContributionsData, increments, coverage]);

  useEffect(() => {
    if (retirementContributionsData && !maxContribution) {
      const paydays = retirementContributionsData.data.number_of_paydays;
      const subMaxAmount =
        retirementContributionsData.data.subscriber_max_amount;
      setMaxContribution(Number((subMaxAmount / paydays).toFixed(2)));
    }
  }, [retirementContributionsData, maxContribution]);

  if (
    !selectedRadio ||
    !retirementContributionsData ||
    increments.length === 0
  ) {
    return null;
  }

  return (
    <div className="px-3_5 px-md-5">
      <div className="row ml-0 mt-3" data-testid="contribution-401k-header">
        <h2 className="mb-0">{t('selectContribution')}</h2>
      </div>

      <Contribution401k.RadioButtons
        orgEnrollmentMethod={
          retirementContributionsData?.data.org_enrollment_method
        }
        setErrorMessage={setErrorMessage}
      />

      <Contribution401k.Inputs
        numberInputRef={numberInputRef}
        increments={increments}
        maxContribution={maxContribution}
        setErrorMessage={setErrorMessage}
      />

      {errorMessage && (
        <Contribution401k.ErrorMessage errorMessage={errorMessage} />
      )}

      <Contribution401k.Banner
        increments={increments}
        maxContribution={maxContribution}
      />
    </div>
  );
};

Contribution401k.RadioButtons = function RadioButtons(
  props: Contribution401kRadioProps
) {
  const {
    selectedRadio,
    setAmountPercentage,
    setEeContribution,
    setSelectedRadio,
  } = useContribution401kContext();
  const { orgEnrollmentMethod, setErrorMessage } = props;
  const { t } = useTranslation('pages.financial');
  const showRadios = orgEnrollmentMethod === 'annual_and_percent';
  const perPayText = useSelector(selectPerPayPeriodText);

  function handleRadioChange(value: RadioOption) {
    setAmountPercentage(null);
    setEeContribution(null);
    setErrorMessage(null);
    setSelectedRadio(value);
  }

  if (!showRadios) {
    return null;
  }

  return (
    <div
      className="row ml-0"
      role="radiogroup"
      aria-label={t('newHSA.contributionFrequency')}
    >
      <div className="mr-4">
        <ps-radio-button
          key="contribution-benefit"
          input-id="contribution-benefit-per-percentage"
          name="radio-contribution-benefit"
          label={t('percentage')}
          checked={selectedRadio === 'percentage' ? true : null}
          onFocus={() => handleRadioChange('percentage')}
        />
      </div>

      <ps-radio-button
        key="contribution-benefit"
        input-id="contribution-benefit-period"
        name="radio-contribution-benefit"
        label={`${perPayText}`}
        checked={selectedRadio === 'perPayPeriod' ? true : null}
        onFocus={() => handleRadioChange('perPayPeriod')}
      />
    </div>
  );
};

Contribution401k.Inputs = function Inputs(props: Contribution401kInputsProps) {
  const {
    selectedRadio,
    eeContribution,
    amountPercentage,
    setAmountPercentage,
    setEeContribution,
  } = useContribution401kContext();
  const { numberInputRef, increments, maxContribution, setErrorMessage } =
    props;
  const perPayText = useSelector(selectPerPayPeriodText);

  function handlePercentageChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const inputValue = Number(event.target.value);
    if (!inputValue) {
      setEeContribution(null);
      setAmountPercentage(null);
    } else {
      const subscriberPremium = increments.find(
        (inc) => inc.increment === inputValue
      )?.subscriber_premium;
      setEeContribution(Number(subscriberPremium));
      setAmountPercentage(inputValue);
    }
  }

  function handlePayPeriodChange(event: React.ChangeEvent<HTMLInputElement>) {
    const inputValue = Number(event.target.value);

    if (Number.isNaN(inputValue)) {
      setEeContribution(null);
    } else if (inputValue > maxContribution) {
      setErrorMessage('Cannot exceed the maximum per period amount');
      setEeContribution(null);
    } else {
      setEeContribution(inputValue);
      setErrorMessage(null);
    }
  }

  if (increments.length === 0) {
    return null;
  }

  if (selectedRadio === 'percentage') {
    return (
      <div className="row align-items-end mt-3">
        <div className="col col-md-3">
          <PsSelectTuner
            inputId="personal-contribution-percentage"
            label="Choose Percentage"
            value={amountPercentage ? amountPercentage?.toString() : ''}
            onInput={(e) => handlePercentageChange(e as any)}
          >
            <option value="">Select</option>
            {increments.map((inc) => {
              return (
                <option key={inc.increment} value={inc.increment}>
                  {inc.increment}%
                </option>
              );
            })}
          </PsSelectTuner>
        </div>

        <div className="col d-flex flex-column justify-content-end justify-content-md-start">
          {getPremiumStyled({
            premium: String(eeContribution || 0),
            valueOnly: RETURN_DISPLAY,
            perPayText: perPayText,
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="row col-12 mb-4">
      <ps-numeric-field
        ref={numberInputRef}
        input-id="personal-contribution-per-period"
        label="Personal Contribution"
        placeholder="0.00"
        help-text="Enter the dollar amount you would like to contribute per pay period."
        icon="currency-dollar"
        value={eeContribution}
        onBlur={handlePayPeriodChange}
        onKeyUp={handlePayPeriodChange}
        icon-left
      />
    </div>
  );
};

Contribution401k.ErrorMessage = function ErrorMessage({
  errorMessage,
}: {
  errorMessage: string;
}) {
  return (
    <BeneficiaryDataCardAllocationErrorStyled
      className="w-100"
      variant="caption"
      display="block"
    >
      {errorMessage}
    </BeneficiaryDataCardAllocationErrorStyled>
  );
};

Contribution401k.Banner = function Banner({
  increments,
  maxContribution,
}: {
  increments: RetirementContributionsResponseDto['data']['increments'];
  maxContribution: number;
}) {
  const { selectedRadio } = useContribution401kContext();
  if (increments.length === 0) {
    return null;
  }

  const minimumPercentage = increments[0].increment;
  const maximumPercentage = increments.slice(-1)[0].increment;
  const bannerMessage =
    selectedRadio === 'percentage'
      ? `Percentage designation must be made in whole or half percentage values, for example 2% or 2.5%. The minimum contribution is ${minimumPercentage}% and the maximum is ${maximumPercentage}%.`
      : `The minimum pay period contribution is $0.01 and the maximum pay period contribution is ${convertCurrency(String(maxContribution))}.`;

  return (
    <div className="mt-3 mb-4">
      <PSBannerWrapper message={bannerMessage} level="info" icon />
    </div>
  );
};

Contribution401k.Footer = function Footer({
  showEsignatureModal,
  plan,
  buttonDisabled,
  benefitPlans,
}: Contribution401kFooterProps) {
  const {
    amountPercentage,
    selectedRadio,
    retirementContributionsData,
    eeContribution,
    benefit,
    enrollmentContextType,
    updateElection,
    electedRiders,
  } = useContribution401kContext();
  const { t } = useTranslation('pages.financial');

  const emptyFunction = () => {
    return;
  };

  const benefitsPlanData = benefitPlans?.find(
    (bPlan) => plan.plan_id === bPlan.plan_id
  );

  function electPlan(additionalPlanData?: AdditionalPlanElectionDataDto) {
    const riderMapping = mapElectedRiders(electedRiders);
    const params = {
      org_benefit_id: benefit.org_benefit_id,
      election: {
        org_plan_id: plan.plan_id,
        coverage_level_id: benefitsPlanData?.level_id,
      },
      ...(additionalPlanData?.esign_checked && {
        esign_checked: additionalPlanData.esign_checked,
      }),
      ...(additionalPlanData?.esign_password && {
        esign_password: additionalPlanData.esign_password,
      }),
      enrollment_context_type: enrollmentContextType,
      ...riderMapping,
    } as any;

    if (selectedRadio === 'percentage' && amountPercentage) {
      params.election.increment = amountPercentage;
    } else if (selectedRadio === 'perPayPeriod' && eeContribution) {
      const paydays = retirementContributionsData?.data.number_of_paydays || 1;
      params.election.subscriber_amount = eeContribution * paydays;
    }

    updateElection(params);
  }

  return (
    <div className="border-top px-5 py-4 text-center text-md-left">
      {showEsignatureModal ? (
        <EsignatureModal
          isFinancial={true}
          setEsignElectionData={electPlan}
          carrierLogoUrl={plan.carrier_logo_url}
          carrierName={plan.carrier}
          data={plan.esign_data}
          planId={plan.plan_id}
          setModalRequired={emptyFunction}
        />
      ) : (
        <PsButtonTuner
          buttonId="submit-contribution-button"
          label={t('looksGood')}
          level="primary"
          onClick={() => electPlan()}
          disabled={buttonDisabled}
        />
      )}
    </div>
  );
};
